export function deriveStatus(s) {
    if (s.receiptRegistered)
        return 'closed';
    if (s.filed)
        return 'filed';
    if (s.korMode)
        return 'not_required';
    if (s.scopeBlocked)
        return 'out_of_scope';
    if (s.openQuestions > 0)
        return 'questions_open';
    if (!s.quarterEnded || !s.bankCoverageComplete || !s.hasDraft)
        return 'collecting';
    if (s.reviewState === 'running')
        return 'in_review';
    if (s.reviewState === 'differences' || s.unresolvedDifferences > 0)
        return 'review_differences';
    if (s.reviewState === 'passed')
        return s.yearEndOpen ? 'draft_ready' : 'ready_to_file';
    return 'draft_ready';
}
/**
 * Gate 1: all question cards answered. Gate 2: independent review passed on the current draft.
 * yearEnd: in Q4 the year-end check (TS-06) must be answered before authorizing.
 */
export function gates(s) {
    return {
        gate1: { ok: s.openQuestions === 0, openQuestions: s.openQuestions },
        gate2: {
            ok: s.reviewState === 'passed' && s.unresolvedDifferences === 0,
            state: s.reviewState,
            unresolved: s.unresolvedDifferences,
        },
        yearEnd: { ok: !s.yearEndOpen },
    };
}
/* ───────────── Q4 year-end check (TS-06) ───────────── */
/**
 * Year-end corrections that v1 cannot compute and that belong in the last return of the year (Belastingdienst,
 * "Laatste btw-aangifte van het jaar"): private use of a company car (2,7 % / 1,5 % of the catalogue price, rubriek 1d),
 * staff provisions, gifts and business gifts above the € 227 threshold (BUA), and private use of goods or services
 * whose VAT was deducted. Any 'yes' puts the Q4 return out of scope: refer to a partner accountant.
 */
export const YEAR_END_ITEMS = ['companyCarPrivate', 'giftsOverThreshold', 'privateUse'];
/** The check applies to the last quarterly return of the year. */
export function yearEndRequired(quarter) {
    return /-Q4$/.test(quarter);
}
/** Items answered 'yes' (each one blocks the return). */
export function yearEndBlocks(a) {
    return a ? YEAR_END_ITEMS.filter((k) => a[k] === true) : [];
}
export const STATUS_META = {
    out_of_scope: { status: 'out_of_scope', tone: 'danger', icon: 'ban', nextAction: 'refer', urgency: 0 },
    questions_open: { status: 'questions_open', tone: 'info', icon: 'message-circle-question', nextAction: 'answer_questions', urgency: 1 },
    review_differences: { status: 'review_differences', tone: 'warning', icon: 'triangle-alert', nextAction: 'view_differences', urgency: 2 },
    collecting: { status: 'collecting', tone: 'neutral', icon: 'inbox', nextAction: 'upload_documents', urgency: 3 },
    draft_ready: { status: 'draft_ready', tone: 'review', icon: 'file-text', nextAction: 'view_concept', urgency: 4 },
    in_review: { status: 'in_review', tone: 'review', icon: 'loader', nextAction: 'busy', urgency: 5 },
    ready_to_file: { status: 'ready_to_file', tone: 'success', icon: 'circle-check', nextAction: 'compare_portal', urgency: 6 },
    filed: { status: 'filed', tone: 'submitted', icon: 'send', nextAction: 'upload_receipt', urgency: 7 },
    closed: { status: 'closed', tone: 'neutral', icon: 'check', nextAction: 'view_return', urgency: 8 },
    not_required: { status: 'not_required', tone: 'neutral', icon: 'check', nextAction: 'view_kor', urgency: 9 },
};
export const STEPS = ['gegevens', 'vragen', 'concept', 'controle', 'indienen', 'bevestiging'];
/** Which of the 6 steps is current for a status. */
export function currentStep(status) {
    switch (status) {
        case 'collecting':
        case 'not_required':
            return 'gegevens';
        case 'questions_open':
            return 'vragen';
        case 'draft_ready':
        case 'out_of_scope':
            return 'concept';
        case 'in_review':
        case 'review_differences':
            return 'controle';
        case 'ready_to_file':
            return 'indienen';
        case 'filed':
            return 'bevestiging';
        case 'closed':
            return 'bevestiging';
    }
}
export function stepStates(status) {
    const cur = currentStep(status);
    const idx = STEPS.indexOf(cur);
    const out = {};
    STEPS.forEach((s, i) => {
        out[s] = status === 'closed' ? 'done' : i < idx ? 'done' : i === idx ? 'current' : 'upcoming';
    });
    return out;
}
/** Overview sorting: most urgent first, then by deadline/entity name. */
export function compareUrgency(a, b) {
    return STATUS_META[a].urgency - STATUS_META[b].urgency;
}
/**
 * `authorized`: the current draft carries a valid confirmation ("Bevestigen en naar het portaal"). Only then is a
 * ready-to-file return in the portal sub-step; a confirmation that was revoked (figures changed) sends it back to
 * "check", which is how re-confirmation after a change shows up in every view.
 */
export function journeyPosition(status, opts = {}) {
    const at = (step, sub = null, extra = {}) => ({
        step, sub, waiting: false, blocked: false, ...extra,
    });
    switch (status) {
        case 'collecting':
            return at('step1');
        case 'questions_open':
            return at('step2');
        case 'out_of_scope':
            return at('step3', 'check', { blocked: true });
        case 'in_review':
            return at('step3', 'check', { waiting: true });
        case 'draft_ready':
        case 'review_differences':
            return at('step3', 'check');
        case 'ready_to_file':
            return at('step3', opts.authorized ? 'portal' : 'check');
        case 'filed':
            return at('step3', 'receipt');
        case 'closed':
        case 'not_required':
            return at('done');
    }
}
/** Steps in order, for rendering the three numbered steps / pills. */
export const JOURNEY_STEPS = ['step1', 'step2', 'step3'];
export const SUBMIT_SUBSTEPS = ['check', 'portal', 'receipt'];
/** done / current / todo of one journey step (or sub-step) relative to a position. */
export function journeyStepState(pos, step) {
    if (pos.step === 'done')
        return 'done';
    const i = JOURNEY_STEPS.indexOf(step);
    const cur = JOURNEY_STEPS.indexOf(pos.step);
    return i < cur ? 'done' : i === cur ? 'current' : 'todo';
}
export function submitSubStepState(pos, sub) {
    if (pos.step === 'done')
        return 'done';
    if (pos.step !== 'step3' || !pos.sub)
        return 'todo';
    const i = SUBMIT_SUBSTEPS.indexOf(sub);
    const cur = SUBMIT_SUBSTEPS.indexOf(pos.sub);
    return i < cur ? 'done' : i === cur ? 'current' : 'todo';
}
/**
 * The one statement the user ticks on "Controleren en indienen" (2026-10). It replaces the earlier three checkboxes
 * and covers three points: (1) I have looked at these figures, (2) to the best of my knowledge the statements, invoices
 * and answers I provided are complete and correct, (3) I file the return myself in Mijn Belastingdienst Zakelijk and am
 * responsible for what I file. v2 (2026-10-20): the user vouches for their own input, not our calculation. The wording is
 * the i18n key `submit.confirm.statement`; bump the version whenever that wording changes in substance.
 */
export const CONFIRM_STATEMENT = 'submit-v2';
//# sourceMappingURL=status.js.map